import { action, computed, observable } from 'mobx';
import { LayoutRoutesConfig } from 'typings/configs';

import { RootStore } from './root';

export class LayoutStore {
    rootStore: RootStore;

    @observable private layoutConfig?: LayoutRoutesConfig;

    constructor(rootStore: RootStore, initialState?: InitialState['layoutStore']) {
        this.rootStore = rootStore;
        if (initialState) {
            this.setInitialState(initialState);
        }
    }

    setInitialState(initialState: InitialState['layoutStore']) {
        Object.assign(this, initialState);
    }

    @action
    setLayoutConfig(layoutConfig: LayoutRoutesConfig) {
        this.layoutConfig = layoutConfig;
    }

    @computed
    get currentPath() {
        const location = this.rootStore.historyStore.location;
        return `${location.pathname}${location.search}`;
    }

    @computed
    get currentLayoutType() {
        const { isAnonymousUser } = this.rootStore.userStore;
        const hasSidebar = Boolean(this.layout?.sidebar);
        const hasNavbar = Boolean(this.layout?.navbarOnMobile);
        const isPersonalAreaLayout = hasNavbar || hasSidebar;

        return isAnonymousUser && isPersonalAreaLayout
            ? 'anonymousArea'
            : isPersonalAreaLayout
              ? 'personalArea'
              : 'public';
    }

    @computed get isInitialized() {
        return Boolean(this.layoutConfig);
    }

    @computed
    get layout() {
        const { isProfessional, isClient } = this.rootStore.userStore;
        let layoutRef = this.layoutConfig?.routes.find((route) => {
            const regExp = new RegExp(route.url.join('|'));
            return regExp.test(this.currentPath);
        })?.layoutRef;

        if (!(isProfessional || isClient) && layoutRef === 'navbar-on-mobile') {
            layoutRef = 'authorized';
        }

        return this.layoutConfig?.layouts.find((layout) => layout.ref === layoutRef);
    }

    @computed
    get shouldDisableRegionModals() {
        return this.layout.userRegionModals?.disable;
    }

    @computed
    get hasFooter() {
        return Boolean(this.layout?.footer);
    }

    @computed
    get footerProps() {
        return this.layout?.footer;
    }

    @computed
    get hasHeader() {
        return Boolean(this.layout?.header) || this.currentLayoutType === 'anonymousArea';
    }

    @computed
    get headerProps() {
        return this.layout?.header;
    }

    @computed
    get hasSidebar() {
        return Boolean(this.layout?.sidebar) && this.currentLayoutType !== 'anonymousArea';
    }

    @computed
    get sidebarProps() {
        return this.layout?.sidebar;
    }

    @computed
    get hasNavbarOnMobile() {
        return Boolean(this.layout?.navbarOnMobile) && this.currentLayoutType !== 'anonymousArea';
    }

    @computed
    get navbarProps() {
        return this.layout?.navbar;
    }

    @computed
    get shouldHideChatsButtonOnMobile() {
        return Boolean(this.layout.chatsButtonOnMobile?.hide);
    }

    @computed
    get hideUseDeskRoutes() {
        return (this.layoutConfig?.hideUseDeskRoutes || []).map((route) => new RegExp(route));
    }

    @computed
    get hideCookieConsentRoutes() {
        return (this.layoutConfig?.hideCookieConsentRoutes || []).map((route) => new RegExp(route));
    }

    @computed
    get hideHeaderOnMobile() {
        const { isProfessional, isClient } = this.rootStore.userStore;
        return Boolean(this.layout?.header?.hide && (isProfessional || isClient));
    }
}
